import type { SupabaseClient } from '@supabase/supabase-js';
import type { AxiosRequestConfig } from 'axios';
import type { EmailCodeVerifyResponse, VerifyAuthResponse } from '@common/api/authService';
import { buildOnboardingRoutePreservingInvite } from '@/components/Onboarding/inviteRedirect';
import { resolveBustlyWebRedirect } from '@/utils/webRedirect';
import { isPublishedAppShareRedirect } from '@/pages/PublishedAppShare/shareHandoff';
import { readReferralCode } from '@common/referral/referralAttribution';

export const EMAIL_OTP_RESEND_COOLDOWN_SECONDS = 60;

export interface SendEmailOtpParams {
    email: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface VerifyEmailOtpParams {
    email: string;
    token: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface EmailCodeAuthClient {
    sendEmailCode(email: string, axiosRequestConfig?: AxiosRequestConfig): Promise<unknown>;
    verifyEmailCode(
        email: string,
        code: string,
        axiosRequestConfig?: AxiosRequestConfig,
    ): Promise<EmailCodeVerifyResponse>;
}

const shouldResumePublishedAppShare = (search: string): boolean => (
    typeof window !== 'undefined'
    && isPublishedAppShareRedirect(
        search,
        window.sessionStorage,
        window.location?.origin || 'https://www.bustly.ai',
    )
);

export function getEmailOtpPostSignInRedirect(
    search: string,
    profile: Pick<VerifyAuthResponse, 'workspace'> | null,
): string | null {
    const params = new URLSearchParams(search);
    if (params.get('connect') || params.get('oauth_context')) {
        return null;
    }

    if (shouldResumePublishedAppShare(search)) {
        return resolveBustlyWebRedirect(params.get('redirect'), '/apps/share');
    }

    // A referral always starts creation of a new attributed workspace, including
    // for an account that already owns or belongs to another workspace. Still
    // wait for a verified profile so a partial auth response cannot redirect.
    if (readReferralCode(search)) {
        if (!profile || profile.workspace === undefined) return null;
        return `/admin${buildOnboardingRoutePreservingInvite(search)}`;
    }

    if (profile && !profile.workspace) {
        return `/admin${buildOnboardingRoutePreservingInvite(search)}`;
    }

    return resolveBustlyWebRedirect(params.get('redirect'));
}

export function getPasswordSetupRedirect(
    search: string,
    userType: 'new' | 'invited' | 'existing' | 'unknown',
): string {
    if (shouldResumePublishedAppShare(search)) {
        const params = new URLSearchParams(search);
        return resolveBustlyWebRedirect(params.get('redirect'), '/apps/share');
    }

    if (userType === 'new') {
        return `/admin${buildOnboardingRoutePreservingInvite(search)}`;
    }

    const params = new URLSearchParams(search);
    return resolveBustlyWebRedirect(params.get('redirect'));
}

export async function sendEmailOtp(
    authClient: EmailCodeAuthClient,
    params: SendEmailOtpParams,
) {
    await authClient.sendEmailCode(params.email, params.axiosRequestConfig);
}

export async function verifyEmailOtp(
    authClient: EmailCodeAuthClient,
    supabase: SupabaseClient,
    params: VerifyEmailOtpParams,
) {
    const session = await authClient.verifyEmailCode(
        params.email,
        params.token,
        params.axiosRequestConfig,
    );
    if (!session.access_token || !session.refresh_token) {
        throw new Error('Verification succeeded without a complete session.');
    }

    const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
    });

    if (error) throw error;
}
